import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BlogComments = ({ blogId, slug }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (slug) fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blogs/${slug}/comments`);
      const data = await res.json();
      if (data.success) setComments(data.data);
    } catch (e) {
      console.error('Error fetching comments:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.comment.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/blogs/${slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name.trim(), comment: formData.comment.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setComments((prev) => [data.data, ...prev]);
        setFormData({ name: '', comment: '' });
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setShowForm(false);
        }, 3000);
      } else {
        setError(data.message || 'Failed to post comment. Please try again.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <section className="blog_comments">
      <div className="comments_header">
        <h3 className="comments_title">
          <i className="fa-regular fa-comments"></i>
          Comments
          {comments.length > 0 && (
            <span className="comments_count">({comments.length})</span>
          )}
        </h3>
        {!showForm && (
          <button className="add_comment_btn" onClick={() => setShowForm(true)}>
            <i className="fa-regular fa-pen-to-square"></i>
            Leave a Comment
          </button>
        )}
      </div>

      {showForm && (
        <div className="comment_form_wrapper">
          {submitted ? (
            <div className="comment_success">
              <div className="success_icon">
                <i className="fa-solid fa-circle-check"></i>
              </div>
              <h4>Thank You!</h4>
              <p>Your comment has been posted successfully.</p>
            </div>
          ) : (
            <form className="comment_form" onSubmit={handleSubmit}>
              <div className="form_header">
                <h4>Leave a Comment</h4>
              </div>
              {error && (
                <div className="form_error" style={{ color: '#ef4444', marginBottom: '12px', fontSize: '14px' }}>
                  {error}
                </div>
              )}
              <div className="form_group">
                <label htmlFor="comment_name">
                  Your Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="comment_name"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your name"
                  required
                  className="form_input"
                />
              </div>
              <div className="form_group">
                <label htmlFor="comment_text">
                  Your Comment <span className="required">*</span>
                </label>
                <textarea
                  id="comment_text"
                  value={formData.comment}
                  onChange={(e) => setFormData((p) => ({ ...p, comment: e.target.value }))}
                  placeholder="Share your thoughts..."
                  required
                  rows={4}
                  className="form_textarea"
                />
              </div>
              <div className="form_actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="cancel_btn"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit_btn" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Post Comment
                      <i className="fa-solid fa-paper-plane"></i>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {!loading && comments.length === 0 && !showForm && (
        <div className="no_comments">
          <div className="no_comments_icon">
            <i className="fa-regular fa-comment-dots"></i>
          </div>
          <h4>No Comments Yet</h4>
          <p>Be the first to share your thoughts on this article!</p>
          <button className="start_discussion_btn" onClick={() => setShowForm(true)}>
            Start the Discussion
            <i className="fa-solid fa-arrow-right"></i>
          </button>
        </div>
      )}

      {comments.length > 0 && (
        <ul className="comments_list">
          {comments.map((comment) => (
            <li key={comment._id} className="comment_item">
              <div className="comment_avatar">
                <div className="avatar_placeholder">
                  {comment.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="comment_content">
                <div className="comment_header">
                  <h5 className="comment_author">{comment.name}</h5>
                  <time className="comment_date">{formatDate(comment.createdAt)}</time>
                </div>
                <p className="comment_text">{comment.comment}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default BlogComments;
